import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { LogIn, Bike, User, UserRound, ShieldCheck } from "lucide-react";
import ForgotPasswordModal from "@/components/ForgotPasswordModal";
import PasswordInput from "@/components/PasswordInput";
import BackButton from "@/components/BackButton";
import QuickLoginPanel from "@/components/QuickLoginPanel";
import { loadSavedCreds, saveCreds, clearCreds } from "@/utils/quickLogin";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function startGoogleAuth() {
  const redirectUrl = window.location.origin + "/auth-callback";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
}

export default function Login() {
  const { login, user } = useAuth();
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const asRole = sp.get("as") || "customer";
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(true);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [showForgot, setShowForgot] = useState(false);
  // If saved creds + PIN exist, hide the full form and show the QuickLoginPanel instead.
  const [showFullForm, setShowFullForm] = useState(!loadSavedCreds());
  const idName = useMemo(() => `i_${Math.random().toString(36).slice(2, 10)}`, []);
  const pwName = useMemo(() => `p_${Math.random().toString(36).slice(2, 10)}`, []);

  // Pre-fill saved credentials on first render.
  useEffect(() => {
    const saved = loadSavedCreds();
    if (!saved) return;
    if (saved.identifier) setIdentifier(saved.identifier);
    if (saved.password) setPassword(saved.password);
    setRemember(true);
  }, []);

  useEffect(() => {
    if (user) {
      if (user.role === "admin") nav("/admin");
      else if (user.role === "driver") nav("/driver");
      else if (user.role === "restaurant") nav("/restaurant");
      else nav("/");
    }
  }, [user, nav]);

  const performLogin = async (idTrim, pw, shouldRemember) => {
    setBusy(true); setErr("");
    const r = await login(idTrim, pw);
    setBusy(false);
    if (!r.ok) { setErr(r.error); return false; }
    if (shouldRemember) saveCreds(idTrim, pw);
    else clearCreds();
    return true;
  };

  const submit = async (e) => {
    e.preventDefault();
    await performLogin(identifier.trim(), password, remember);
  };

  const onQuickUnlock = async (savedCreds) => {
    await performLogin(savedCreds.identifier, savedCreds.password, true);
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 gradient-warm relative">
      <div className="absolute top-4 right-4">
        <BackButton to="/welcome"/>
      </div>
      <div className="w-full max-w-md bg-white rounded-3xl talabya-shadow-soft p-8 animate-fade-up">
        <Link to="/welcome" className="flex items-center gap-2 mb-6" data-testid="login-brand">
          <img src="/icon-192.png" alt="طلبية" className="w-12 h-12 rounded-xl object-cover" style={{boxShadow:"0 3px 8px rgba(37,71,208,0.3)"}} />
          <div>
            <div className="font-black text-xl" style={{fontFamily:'Cairo'}}>طلبية</div>
            <div className="text-xs text-[var(--text-secondary)] -mt-1">مرحباً بعودتك</div>
          </div>
        </Link>

        <h1 className="text-3xl font-black mb-1" style={{fontFamily:'Cairo'}}>تسجيل الدخول</h1>
        <p className="text-sm text-[var(--text-secondary)] mb-6">أدخل بريدك الإلكتروني أو رقم هاتفك للمتابعة</p>

        {!showFullForm && (
          <QuickLoginPanel
            onUnlock={onQuickUnlock}
            onFallback={() => setShowFullForm(true)}
          />
        )}
        {busy && !showFullForm && <div className="text-center text-xs text-[var(--text-secondary)] mb-2">جارٍ الدخول...</div>}
        {err && !showFullForm && <div className="text-sm text-red-600 bg-red-50 px-3 py-2 rounded-lg mb-2" data-testid="login-error-quick">{err}</div>}
        <div className={showFullForm ? "" : "hidden"}>
        <form onSubmit={submit} className="flex flex-col gap-4" autoComplete="off">
          <div>
            <label className="text-sm font-bold text-[var(--text-primary)]">البريد الإلكتروني أو رقم الهاتف</label>
            <input
              type="text" required value={identifier} onChange={(e) => setIdentifier(e.target.value)}
              className="mt-1 w-full px-4 py-3 rounded-xl border border-[var(--border)] bg-[var(--surface-alt)] focus:bg-white focus:border-primary-brand focus:outline-none transition text-right"
              placeholder="example@mail.com  أو  09XXXXXXXX"
              dir="ltr"
              inputMode="email"
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck={false}
              name={idName}
              data-testid="login-identifier"
            />
            <div className="text-[11px] text-[var(--text-secondary)] mt-1">يمكنك استخدام أي منهما</div>
          </div>
          <div>
            <label className="text-sm font-bold text-[var(--text-primary)]">كلمة المرور</label>
            <PasswordInput
              required value={password} onChange={(e) => setPassword(e.target.value)}
              inputClassName="mt-1 w-full px-4 py-3 rounded-xl border border-[var(--border)] bg-[var(--surface-alt)] focus:bg-white focus:border-primary-brand focus:outline-none transition"
              placeholder="••••••••"
              autoComplete="new-password"
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck={false}
              name={pwName}
              testId="login-password"
            />
          </div>
          {err && <div className="text-sm text-red-600 bg-red-50 px-3 py-2 rounded-lg" data-testid="login-error">{err}</div>}
          <label className="flex items-center gap-2 cursor-pointer select-none -mt-1" data-testid="login-remember-label">
            <input
              type="checkbox"
              checked={remember}
              onChange={(e) => setRemember(e.target.checked)}
              className="w-4 h-4 accent-primary-brand cursor-pointer"
              data-testid="login-remember-check"
            />
            <span className="text-xs text-[var(--text-secondary)] flex items-center gap-1">
              <ShieldCheck size={12} className="text-primary-brand"/>
              حفظ بيانات الدخول على هذا الجهاز
            </span>
          </label>
          <button
            disabled={busy}
            className="w-full py-3 rounded-xl bg-primary-brand text-white font-bold text-base hover:opacity-90 disabled:opacity-60 flex items-center justify-center gap-2 talabya-shadow-float"
            data-testid="login-submit">
            <LogIn size={18} /> {busy ? "جاري الدخول..." : "دخول"}
          </button>
          <button
            type="button"
            onClick={() => setShowForgot(true)}
            className="text-sm text-primary-brand hover:underline font-bold text-center -mt-2"
            data-testid="login-forgot-password">
            هل نسيت كلمة المرور؟
          </button>
        </form>
        </div>

        <div className="my-6 flex items-center gap-3">
          <div className="flex-1 h-px bg-[var(--border)]" />
          <span className="text-xs text-[var(--text-secondary)]">أو</span>
          <div className="flex-1 h-px bg-[var(--border)]" />
        </div>

        <button
          type="button"
          onClick={startGoogleAuth}
          className="w-full py-3 rounded-xl bg-white border-2 border-[var(--border)] hover:border-primary-brand hover:bg-blue-50/40 font-black text-sm flex items-center justify-center gap-2 transition-colors"
          data-testid="login-google-btn">
          <svg width="18" height="18" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
          </svg>
          تسجيل الدخول بواسطة Google
        </button>

        {asRole === "customer" && (
          <button
            type="button"
            onClick={() => { try { sessionStorage.setItem("talabya:guest-browse", "1"); } catch {} nav("/"); }}
            className="mt-3 w-full py-3 rounded-xl bg-[var(--surface-alt)] hover:bg-blue-50 border border-dashed border-primary-brand/40 hover:border-primary-brand text-primary-brand font-black text-sm flex items-center justify-center gap-2 transition-colors"
            data-testid="login-guest-btn"
            title="تصفح المطاعم والمنيو دون تسجيل — ستُطلب منك التسجيل عند إتمام الطلب">
            <UserRound size={16}/> الدخول كزائر
          </button>
        )}

        <div className="mt-6 text-center text-sm">
          <span className="text-[var(--text-secondary)]">ليس لديك حساب؟ </span>
          <Link to={`/register?as=${asRole}`} className="font-bold text-primary-brand" data-testid="go-register">إنشاء حساب</Link>
        </div>

        <div className="mt-6 flex justify-around text-xs">
          <Link to="/register?as=customer" className="text-[var(--text-secondary)] hover:text-primary-brand flex items-center gap-1" data-testid="signup-customer"><User size={12}/> تسجيل زبون</Link>
          <Link to="/register?as=driver" className="text-[var(--text-secondary)] hover:text-primary-brand flex items-center gap-1" data-testid="signup-driver"><Bike size={12}/> تسجيل سائق</Link>
        </div>
      </div>
      {showForgot && <ForgotPasswordModal onClose={() => setShowForgot(false)} initialPhone={identifier}/>}
    </div>
  );
}
